import Icon, { IconsVariant } from "@/Components/Icon/Icon";
import Tooltip from "@/Components/Tooltip/Tooltip";
import { targetsWithoutAll } from "@/shared/utils/constant";
import { ReactNode } from "react";
import { useTranslation } from "react-i18next";

export type Target = (typeof targetsWithoutAll)[number];

interface Props<T extends Target> {
  icon: IconsVariant;
  title: string;
  description?: string;
  /** Targets that currently have an override. */
  targets: T[];
  onAdd: (target: Target) => void;
  onRemove: (target: T) => void;
  renderControl: (target: T) => ReactNode;
}

/**
 * Shared layout for "value per order target" overrides (price, VAT, colours):
 * one row per overridden target and one-click chips to add the others.
 */
const TargetOverrideList = <T extends Target>({
  icon,
  title,
  description,
  targets,
  onAdd,
  onRemove,
  renderControl,
}: Props<T>) => {
  const { t } = useTranslation();
  const available = targetsWithoutAll.filter(
    (target) => !targets.some((selected) => selected.value === target.value)
  );

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-start gap-3">
        <Icon
          className="mt-[0.2rem] h-6 w-6 shrink-0 fill-primary"
          variants={icon}
        />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 font-semibold">
            {title}
            {targets.length > 0 && (
              <span className="rounded-full bg-light px-3 py-[0.1rem] text-[1.2rem] text-primary">
                {targets.length}
              </span>
            )}
          </div>
          {description && (
            <p className="mt-1 mb-0 text-small text-text-secondary">
              {description}
            </p>
          )}
        </div>
      </div>

      {targets.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border px-4 py-5 text-center text-small text-text-secondary">
          {t("targetOverrides.empty")}
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {targets.map((target) => (
            <div
              key={target.value}
              className="flex flex-wrap items-center gap-x-4 gap-y-3 rounded-xl border border-border p-3"
            >
              <div className="flex min-w-[14rem] flex-1 items-center gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-light">
                  <Icon
                    className="h-5 w-5 fill-primary"
                    variants={target.icon}
                  />
                </div>
                <span className="truncate font-semibold">
                  {t(target.label)}
                </span>
              </div>
              <div className="flex flex-1 items-center gap-3 sm:min-w-[20rem]">
                <div className="min-w-0 flex-1">{renderControl(target)}</div>
                <Tooltip content={t("targetOverrides.remove")}>
                  <button
                    type="button"
                    aria-label={t("targetOverrides.remove")}
                    onClick={() => onRemove(target)}
                    className="flex h-10 w-10 shrink-0 cursor-pointer items-center justify-center rounded-lg transition-colors hover:bg-error-light"
                  >
                    <Icon className="h-5 w-5 fill-error" variants="trash" />
                  </button>
                </Tooltip>
              </div>
            </div>
          ))}
        </div>
      )}

      {available.length > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          <span className="mr-1 text-small text-text-secondary">
            {t("targetOverrides.add")}
          </span>
          {available.map((target) => (
            <button
              key={target.value}
              type="button"
              onClick={() => onAdd(target)}
              className="flex cursor-pointer items-center gap-2 rounded-full border border-border bg-white px-3 py-[0.4rem] text-small transition-colors hover:border-primary hover:text-primary"
            >
              <Icon className="h-4 w-4 fill-current" variants="plus" />
              {t(target.label)}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default TargetOverrideList;
